import { defineStore } from "pinia";
import api from "@/modules/api";
import { getDisplayDateTime } from "@/modules/common";
import { showLoadingToast } from "vant";
import { GenMedia } from "@/components/mobile/media/Gallery.vue";
import { useMate } from "./mate";
import i18n from "@/locales";
import { useGlobalUser } from "./globalUser";
import { sessionStorageSet } from "@/tools/sessionStorage";
import { useGlobalModal } from "./globalModal";

const PER_PAGE = 12;

export const WAIT_TAB = 0;
export const UNMARKED_TAB = 1;
export const HIGH_TAB = 2;
export const DISCARD_TAB = 3;

const StatusMap = {
  [WAIT_TAB]: undefined,
  [UNMARKED_TAB]: 0,
  [HIGH_TAB]: 1,
  [DISCARD_TAB]: -1,
};

const getDefaultTabList = () => ({
  All: [] as number[],
  MateTrait: [] as number[],
  StoryImage: [] as number[],
  StoryVideo: [] as number[],
  TextGif: [] as number[],
  TextImage: [] as number[],
});

export const useTask = defineStore("task", {
  state: () => ({
    selectedType: 0 as keyof typeof StatusMap,
    selectedSubType: "All" as TaskTab,
    showTaskDetailModal: false,
    showCreateTraitModal: false,
    showMoreActionModal: false,
    activeTaskId: 0,
    taskMap: new Map<number, Task>(),
    unmarkedCount: 0,
    cacheScrollTop: 0,
    list: {
      [WAIT_TAB]: getDefaultTabList(),
      [UNMARKED_TAB]: getDefaultTabList(),
      [HIGH_TAB]: getDefaultTabList(),
      [DISCARD_TAB]: getDefaultTabList(),
    } as {
      [key in keyof typeof StatusMap]: {
        [key in TaskTab]: number[];
      };
    },
    selectGalleryType: HIGH_TAB as 1 | 2,
    galleryListExpired: false,
    galleryList: {
      [UNMARKED_TAB]: [] as number[],
      [HIGH_TAB]: [] as number[],
    },
    galleryMap: new Map<number, GenMedia>(),
    isRefetching: false,
  }),
  getters: {
    currentList(state) {
      return state.list[state.selectedType][state.selectedSubType];
    },
  },
  actions: {
    getTask(id: number) {
      return this.taskMap.get(id);
    },
    async getTaskOrFetch(id: number, force = false) {
      if (!this.taskMap.has(id) || force) {
        const res = await api.readTaskPage({ filter: { id } });
        if (res.page.data[0]) this.taskMap.set(id, res.page.data[0]);
      }
      return this.taskMap.get(id);
    },
    getMedia(id: number): TaskEntity | undefined {
      return this.getTask(id)?.entity;
    },
    getTab(id: number): TaskTab {
      return this.getTask(id)?.tab!;
    },
    async updateUnmarkedCount() {
      const res = await api.readUnmarkedTaskCount();
      this.unmarkedCount = res.data.waitingMarkCount;
    },
    waitingFetch() {
      if (this.isRefetching) return;
      this.isRefetching = true;
      setTimeout(async () => {
        if (this.selectedType === WAIT_TAB && this.list[WAIT_TAB].All.length) {
          await this.fetchTask(true, WAIT_TAB, false);
          this.isRefetching = false;
          this.waitingFetch();
        } else {
          this.isRefetching = false;
        }
      }, 8000);
    },
    async fetchTask(
      loadLatest?: boolean,
      _selectedType?: keyof typeof StatusMap,
      showLoading = true
    ): Promise<boolean> {
      const { selectedType: __selectedType, taskMap, selectedSubType } = this;
      const selectedType = (_selectedType ??
        __selectedType) as keyof typeof StatusMap;

      const currentList = this.list[selectedType][selectedSubType];

      let loading;
      if (showLoading)
        loading = showLoadingToast({
          message: i18n.global.t("Message.loading"),
        });
      const params: any = {
        pagination: { perPage: PER_PAGE },
        sort: { field: "dtUpdate" },
        filter: { status: StatusMap[selectedType], tab: selectedSubType },
      };
      if (selectedType === WAIT_TAB) {
        // 等待中的任务，每次全部更新
        params.pagination.perPage = 999;
        params.sort.order = "asc";
      } else {
        // sort
        params.sort.order = "desc";

        // cursor
        if (currentList.length > 0) {
          const id = loadLatest
            ? currentList[0]
            : currentList[currentList.length - 1];
          params.cursor = btoa(
            JSON.stringify({
              dtUpdate: getDisplayDateTime(
                taskMap.get(id)!.dtUpdate!,
                true,
                true
              ),
              _pointsToNextItems: !loadLatest,
            })
          );
        }
      }
      // fetch
      try {
        const { data } = (await api.readTaskPage(params)).page;
        this.updateUnmarkedCount();
        const method: "unshift" | "push" = loadLatest ? "unshift" : "push";

        if (selectedType === WAIT_TAB) {
          currentList.length = 0;
          this.waitingFetch();
          for (const item of data) {
            this.taskMap.set(item.id!, item);
            currentList[method](item.id!);
          }
        } else {
          for (const item of data) {
            this.taskMap.set(item.id!, item);
            if (!currentList.includes(item.id!)) currentList[method](item.id!);
            if (
              selectedType === HIGH_TAB &&
              !this.galleryList[HIGH_TAB].includes(item.id!)
            )
              this.galleryList[HIGH_TAB][method](item.id!);
            if (
              selectedType === UNMARKED_TAB &&
              !this.galleryList[UNMARKED_TAB].includes(item.id!)
            )
              this.galleryList[UNMARKED_TAB][method](item.id!);
          }
        }

        currentList.sort(
          (a, b) =>
            Number(new Date(this.getTask(b)?.dtUpdate!)) -
            Number(new Date(this.getTask(a)?.dtUpdate!))
        );
        loading && loading.close();
        if (selectedType === WAIT_TAB) return false;
        return data.length < 6 ? false : true;
      } catch (e) {
        loading && loading.close();
        return false;
      }
    },
    async fetchGallery(loadLatest?: boolean) {
      const { selectGalleryType, galleryList, taskMap } = this;
      if (this.galleryListExpired) {
        this.galleryList[UNMARKED_TAB] = [];
        this.galleryList[HIGH_TAB] = [];
        this.galleryListExpired = false;
      }
      const currentList = galleryList[selectGalleryType];
      const params: any = {
        pagination: { perPage: 25 },
        sort: { field: "dtUpdate", order: "desc" },
        filter: { status: StatusMap[selectGalleryType], tab: "All" },
      };
      if (currentList.length > 0) {
        const id = loadLatest
          ? currentList[0]
          : currentList[currentList.length - 1];
        params.cursor = btoa(
          JSON.stringify({
            dtUpdate: getDisplayDateTime(
              taskMap.get(id)!.dtUpdate!,
              true,
              true
            ),
            _pointsToNextItems: !loadLatest,
          })
        );
      }
      params.filter.mateId = useMate().galleryCurrentMate?.id;
      const { data } = (await api.readTaskPage(params)).page;
      const method: "unshift" | "push" = loadLatest ? "unshift" : "push";
      for (const item of data) {
        this.taskMap.set(item.id!, item);
        if (!currentList.includes(item.id!)) currentList[method](item.id!);
        if (
          selectGalleryType === UNMARKED_TAB &&
          !this.list[UNMARKED_TAB].All.includes(item.id!)
        )
          this.list[UNMARKED_TAB].All[method](item.id!);
        if (
          selectGalleryType === HIGH_TAB &&
          !this.list[HIGH_TAB].All.includes(item.id!)
        )
          this.list[HIGH_TAB].All[method](item.id!);
      }
      return data.length < 25 ? false : true;
    },
    // 操作
    // (id) -> 优质
    // (id, true, true) -> 存入相册
    // (id, false, false) -> 废弃
    async like(id: number, isLike = true, toGallery = false) {
      const task = this.getTask(id)!;
      const targetList: keyof typeof StatusMap = isLike
        ? HIGH_TAB
        : DISCARD_TAB;
      const targetStatus: any = StatusMap[targetList];
      const { item } = await api.updateOneTask(id, {
        status: targetStatus,
        copyToAlbum: toGallery ? 1 : 0,
      });
      if (toGallery) sessionStorageSet("reloadMateHome", 1);
      task.status = targetStatus;
      task.dtUpdate = item.dtUpdate;

      if (this.selectedType !== targetList) {
        this._removeTask(id, this.selectedType);
        this._addTask(id, targetList);
      }

      if (this.list[targetList]) this.updateUnmarkedCount();
    },
    _removeTask(id: number, type: keyof typeof StatusMap) {
      // 从 All 和 分类中移出
      if (this.list[type].All.length > 0) {
        const idx = this.list[type].All.indexOf(id);
        if (idx !== -1) this.list[type].All.splice(idx, 1);
      }
      const tab = this.getTab(id);
      if (this.list[type][tab]?.length > 0) {
        const idx = this.list[type][tab].indexOf(id);
        if (idx !== -1) this.list[type][tab].splice(idx, 1);
      }
      // 从相册中移出
      if (type === HIGH_TAB) {
        const idx = this.galleryList[HIGH_TAB].indexOf(id);
        if (idx !== -1) this.galleryList[HIGH_TAB].splice(idx, 1);
      }
      if (type === UNMARKED_TAB) {
        const idx = this.galleryList[UNMARKED_TAB].indexOf(id);
        if (idx !== -1) this.galleryList[UNMARKED_TAB].splice(idx, 1);
      }
    },
    _addTask(id: number, type: keyof typeof StatusMap) {
      if (this.list[type].All.length > 0 && !this.list[type].All.includes(id))
        this.list[type].All.unshift(id);
      const tab = this.getTab(id);
      if (this.list[type][tab].length > 0 && !this.list[type][tab].includes(id))
        this.list[type][tab].unshift(id);
      // 添加到相册
      if (type === HIGH_TAB && !this.galleryList[HIGH_TAB].includes(id))
        this.galleryList[HIGH_TAB].unshift(id);
      if (type === UNMARKED_TAB && !this.galleryList[UNMARKED_TAB].includes(id))
        this.galleryList[UNMARKED_TAB].unshift(id);
    },
    async gif(id: number) {
      const { prompt, mateTraitId, style, mediaId } = this.getMedia(
        id
      ) as TextImage;
      await this.createGifTask({
        sourceId: mediaId,
        prompt,
        mateTraitId: mateTraitId ?? undefined,
        style: style > 0 ? style : undefined,
      });
      this.fetchTask(true, WAIT_TAB);
    },
    async wideImage(id: number) {
      const { tab } = this.getTask(id)!;
      const { mediaId } = this.getMedia(id) as TextImage;
      await api.createOneMediaExtend(mediaId, tab);
      this.fetchTask(true, WAIT_TAB);
    },
    // 删除任务
    async deleteTask(taskId: number) {
      await api.deleteOneTask(taskId);
      this.list[this.selectedType][this.selectedSubType].splice(
        this.list[this.selectedType][this.selectedSubType].indexOf(taskId),
        1
      );
      this.fetchTask(true);
    },
    async createTask(params: PromptJson, goWaitList = true) {
      try {
        await api.createOneTextImageTask(params);
      } catch (e: any) {
        if (e.response?.data?.message?.includes("Failed to connect to")) {
          useGlobalModal().openServerUpgradingModal(true);
        }
        throw e;
      }
      useGlobalUser().refreshUser();
      if (goWaitList) {
        if (history.state.back?.startsWith("/generation/image/list")) {
          window.$router.back();
        }
        setTimeout(() => {
          this.goToWaitListTab();
        }, 100);
      }
    },
    async createGifTask(
      params: Parameters<typeof api.createOneTextGifTask>[0]
    ) {
      await api.createOneTextGifTask(params);
      await this.fetchTask(true, WAIT_TAB);
    },
    async createStoryVideo(
      params: Parameters<typeof api.createOneStoryVideo>[0]
    ) {
      const res = await api.createOneStoryVideo(params);
      this.goToWaitListTab();
      return res.item;
    },
    goToWaitListTab() {
      this.selectedSubType = "All";
      this.selectedType = 0;
      window.$router.replace({
        name: "imageGenerateList",
        hash: "#unfinished",
      });
    },
    goToUnmarkedListTab() {
      this.selectedSubType = "All";
      this.selectedType = 1;
      window.$router.replace({
        name: "imageGenerateList",
        hash: "#unmarked",
      });
    },
    onTaskFinished(type: string, entityId: number) {
      let id = 0;
      if (type === "image") {
        for (const item of this.taskMap.values()) {
          if (item.entity.id === entityId) {
            id = item.id!;
            break;
          }
        }
      } else if (type === "video") {
        for (const item of this.taskMap.values()) {
          if ((item.entity as StoryVideo).storyId === entityId) {
            id = item.id!;
            break;
          }
        }
      }

      this._removeTask(id, WAIT_TAB);
      this.unmarkedCount++;
      this.fetchTask(true, UNMARKED_TAB, false);
    },
    /**
     * @param id task.id
     * @return false-已经完成，true-重试成功
     */
    async retryTask(id: number): Promise<boolean> {
      // 1. 重新获取任务状态，若为已完成则返回
      const res = await api.readTaskPage({ filter: { id } });
      const targetTask = res.page.data[0];
      this.taskMap.set(id, targetTask);
      if (targetTask.dtComplete) {
        await this.fetchTask();
        return false;
      } else {
        await api.retryTask(id);
        await this.fetchTask();
        // 刷新待标记列表
        return true;
      }
    },
    /**
     * 刷新等待中列表 && 获取最新的待标记数据
     * @param reset 重置subType为'ALL', 将等待中tab下所有subType数据清空
     */
    disableCache() {
      this.$reset();
      this.updateUnmarkedCount();
    },
  },
});
